<template>
  <!-- Stops `input` and `change` before they reach the record's admin form, which marks itself unsaved on either.
       Native `input` events are composed, so they cross the shadow boundary. -->
  <div v-if="ready" class="slot-inspector" @input.stop @change.stop>
    <sp-modal position="right" no-bottom-sheet width="min(45rem, 90vw)" :label="t('title')">
      <sp-button slot="activator" variant="outlined" :data-testid="TestId.slotInspector.activator">
        {{ t("activator_label") }}
      </sp-button>

      <SlotInspectorHeader
        slot="header"
        :record-type="recordType"
        :record-name="recordName"
        :ehr-id="ehrId"
        :show-back="showingResults"
        @back="back"
      />

      <div class="slot-inspector__body">
        <Transition name="slot-inspector-fade" mode="out-in" @after-enter="focusEnteredView">
          <SlotInspectorResults v-if="showingResults" ref="results" :report="report" />
          <div v-else>
            <SlotInspectorForm ref="form" :record-type="recordType" />
            <SlotInspectorFormStatus />
          </div>
        </Transition>
      </div>
    </sp-modal>
  </div>
</template>

<script setup>
import { onMounted, provide, ref, useTemplateRef } from "vue";
import { TestId } from "#/test-ids";
import { createSlotInspectorApi } from "./api/slot-inspector";
import SlotInspectorForm from "./components/SlotInspectorForm.ce.vue";
import SlotInspectorFormStatus from "./components/SlotInspectorFormStatus.ce.vue";
import SlotInspectorHeader from "./components/SlotInspectorHeader.ce.vue";
import SlotInspectorResults from "./components/SlotInspectorResults.ce.vue";
import { t, whenI18nReady } from "./i18n";
import { props as appProps } from "./props.js";
import { SLOT_INSPECTOR_KEY, createSlotInspectorStore } from "./store/slot-inspector";

const props = defineProps(appProps);

const store = createSlotInspectorStore({
  api: createSlotInspectorApi(props.previewUrl),
  recordType: props.recordType,
  recordId: props.recordId,
  defaultStartDate: props.defaultStartDate,
});
provide(SLOT_INSPECTOR_KEY, store);
const { showingResults, report, back } = store;

const ready = ref(false);
const form = useTemplateRef("form");
const results = useTemplateRef("results");

// Moves focus into the view that just entered, because the swap removes the control that held it. The results take
// focus on their date range heading, the form on its start date, where the next preview starts.
function focusEnteredView() {
  if (showingResults.value) {
    results.value?.focusHeading();
  } else {
    form.value?.focusStartDate();
  }
}

async function markReady() {
  await whenI18nReady;
  ready.value = true;
}

onMounted(markReady);
</script>

<style>
:host {
  display: block;
  --sp-comp-modal-header-border-bottom: var(--sp-sys-border-width-default) solid var(--sp-sys-color-outline-variant);
  --sp-comp-modal-header-min-height: var(--sp-ref-scale-80);
  --sp-comp-modal-padding: var(--sp-ref-scale-32);
  --sp-comp-modal-backdrop-background-color: var(--sp-ref-palette-black);
  --sp-comp-modal-backdrop-opacity: 0.6;
}
</style>

<style scoped lang="scss">
.slot-inspector {
  &__body {
    padding-block: var(--sp-ref-scale-16);
  }
}

@media (prefers-reduced-motion: no-preference) {
  .slot-inspector-fade-enter-active,
  .slot-inspector-fade-leave-active {
    transition: opacity var(--sp-ref-transition-fade-duration) var(--sp-ref-transition-fade-easing);
  }
}

.slot-inspector-fade-enter-from,
.slot-inspector-fade-leave-to {
  opacity: 0;
}
</style>
